import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import {
  MmButtonModule,
  MmChipModule,
  MmFlexLayoutModule,
  MmIconModule,
  MmSpinnerModule,
} from '@metromarkets/components';
import { QaLocatorsModule } from '@metromarkets/sdk';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { FooterLinkComponent } from '~app/modules/generic/components/footer/footer-link/footer-link.component';
import { MmExpansionModule } from '~shared/modules/mm-expansion/mm-expansion.module';
import { FooterSimplifiedComponent } from './components/footer-simplified/footer-simplified.component';
import { FooterNavigationComponent } from './components/footer/footer-navigation/footer-navigation.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderSimplifiedComponent } from './components/header-simplified/header-simplified.component';
import { AccountDropdownComponent } from './components/header/account-dropdown/account-dropdown.component';
import { HeaderSearchComponent } from './components/header/header-search/header-search.component';
import { HeaderToplineComponent } from './components/header/header-topline/header-topline.component';
import { HeaderComponent } from './components/header/header.component';
import { CategoryItemComponent } from './components/header/mobile-categories/category-item/category-item.component';
import { MobileCategoriesComponent } from './components/header/mobile-categories/mobile-categories.component';
import { MobileMenuComponent } from './components/header/mobile-menu/mobile-menu.component';
import { SuggestionItemComponent } from './components/header/suggestion-item/suggestion-item.component';
import { DesktopSuggestionsListComponent } from './components/header/suggestions-list/desktop-suggestions-list/desktop-suggestions-list.component';
import { MobileSuggestionsListComponent } from './components/header/suggestions-list/mobile-suggestions-list/mobile-suggestions-list.component';
import { SuggestionsListComponent } from './components/header/suggestions-list/suggestions-list.component';
import { UserBoxComponent } from './components/header/user-box/user-box.component';
import { MenuAimDirective } from './directives/menu-aim.directive';
import { BoldPipe } from './pipes/bold.pipe';
import { metaReducers, reducers } from './store';
import { BUYER_HEADER_FEATURE_NAME } from './store/feature-name';
import { SearchEffects } from './store/search';
import { SearchSuggestionsEffects } from './store/search-suggestions/search-suggestions.effects';

import { CarouselModule } from 'ngx-owl-carousel-o';
import { MiniShoppingCartEmptyComponent } from '~app/modules/generic/components/header/mini-shopping-cart-empty';
import { CategoryEffects } from '~app/modules/generic/store/category';
import { DisableClickEventModule } from '~app/modules/storyblok/directives/disable-click-event/disable-click-event.module';
import { StoryblokEditableModule } from '~app/modules/storyblok/directives/storyblok-editable/storyblok-editable.module';
import { LazyLoadImageModule } from '~shared/directives/lazyload/lazy-load-image.module';
import { BasePageModule } from '~shared/modules/base-page/base-page.module';
import { MmPopoverModule } from '~shared/modules/popover/popover.module';
import { PriceTypeTogglerModule } from '~shared/modules/price-type-toggler/price-type-toggler.module';
import { ProductCardModule } from '~shared/modules/product-card/product-card.module';
import { TooltipModule } from '~shared/modules/tooltip/tooltip.module';
import { FrPostfixModule } from '~shared/pipes/fr-postfix/fr-postfix.module';
import { BasketPassiveModule } from '~shared/svgs/basket-passive/basket-passive.module';
import { ChevronLeftModule } from '~shared/svgs/chevron-left/chevron-left.module';
import { ChevronRightModule } from '~shared/svgs/chevron-right/chevron-right.module';
import { MailModule } from '~shared/svgs/mail/mail.module';
import { PhoneModule } from '~shared/svgs/phone/phone.module';
import { NewsModule } from '~shared/svgs/news/news.component.module';
import { SearchModule } from '~shared/svgs/search/search.module';
import { AccountFlyoutContextComponent } from './components/header/account-flyout-context/account-flyout-context.component';
import { AccountFlyoutFailoverComponent } from './components/header/account-flyout-failover/account-flyout-failover.component';
import { EmployeeFlyoutComponent } from './components/header/employee-flyout/employee-flyout.component';
import { EmployeeProfileBoxComponent } from './components/header/employee-profile-box/employee-profile-box.component';
import { ExternalLinksComponent } from './components/header/external-links/external-links.component';
import { HeaderBuyAgainComponent } from './components/header/header-buy-again/header-buy-again.component';

import { HeaderToplineLinksComponent } from './components/header/header-topline/header-topline-links/header-topline-links.component';
import { MiniShoppingCartComponent } from './components/header/mini-shopping-cart/mini-shopping-cart.component';
import { EditorialLinkListComponent } from './components/header/offer-and-inspiration/editorial-link-list/editorial-link-list.component';
import { MenuNavigationBannerComponent } from './components/header/offer-and-inspiration/menu-navigation-banner/menu-navigation-banner.component';
import { MobileMenuItemComponent } from './components/header/offer-and-inspiration/mobile-menu-item/mobile-menu-item.component';
import { PriceSwitchComponent } from './components/header/price-switch/price-switch.component';
import { PromoCategoriesComponent } from './components/header/promo-categories/promo-categories.component';
import { PromoImageLinkComponent } from './components/header/promo-image-link/promo-image-link.component';
import { SecondaryHeaderComponent } from './components/header/secondary-header/secondary-header.component';
import { TopCategoriesNavbarSecondLevelComponent } from './components/header/top-categories-navbar-second-level/top-categories-navbar-second-level.component';
import { TopCategoriesNavbarComponent } from '~app/modules/generic/components/header/top-categories-navbar/top-categories-navbar.component';
import { NavigationComponent } from './components/navigation/navigation.component';
import { CarouselEffects } from './store/carousel';
import { CategoriesEffects } from './store/categories';
import { CartNavigatorModule } from '~shared/modules/cart-navigator/cart-navigator.module';
import { MobileSidenavComponent } from './components/header/mobile-sidenav/mobile-sidenav.component';
import { CategoryBubbleModule } from '~shared/components/category-bubble/category-bubble.module';
import { MobileParentCategoryNavigationComponent } from './components/header/mobile-sidenav/mobile-parent-categories-navigation/mobile-parent-categories-navigation.component';
import { RegionSelectorModule } from '~shared/modules/region-selector/region-selector.module';
import { CrawlableCategoriesComponent } from './components/header/mobile-sidenav/crawlable-categories/crawlable-categories.component';
import { CoBrowseModule } from '~shared/svgs/co-browser/co-browse.module';
import { ReBuyModule } from '~shared/svgs/re-buy/re-buy.module';
import { ApprovalsModule } from '~shared/svgs/approvals/approvals.module';
import { CartV2Module } from '~shared/svgs/cart-v2/cart-v2.module';
import { ListModule } from '~shared/svgs/list/list.module';
import { HeaderYourAssortmentComponent } from './components/header/header-shopping-list/header-your-assortment.component';
import { DesktopSwipeModule } from '~shared/directives/desktopSwipe/desktopSwipe.module';
import { CalenderModule } from '~shared/svgs/calender/calender.module';
import { NewBadgeComponent } from '~app/modules/generic/components/header/new-badge/new-badge.component';
import { BoxModule } from '~shared/svgs/box/box.module';
import { SuperSavingsModule } from '~shared/svgs/super-savings/super-savings.module'; 
import { AllOffersModule } from '~shared/svgs/all-offers/all-offers.module';

@NgModule({
  declarations: [
    HeaderComponent,
    HeaderToplineComponent,
    HeaderSimplifiedComponent,
    HeaderToplineLinksComponent,
    HeaderSearchComponent,
    SuggestionsListComponent,
    MobileSuggestionsListComponent,
    DesktopSuggestionsListComponent,
    SuggestionItemComponent,
    CategoryItemComponent,
    FooterComponent,
    FooterSimplifiedComponent,
    FooterNavigationComponent,
    FooterLinkComponent,
    UserBoxComponent,
    MenuAimDirective,
    BoldPipe,
    AccountDropdownComponent,
    HeaderSimplifiedComponent,
    MobileMenuComponent,
    MobileCategoriesComponent,
    ExternalLinksComponent,
    SecondaryHeaderComponent,
    PriceSwitchComponent,
    NavigationComponent,
    TopCategoriesNavbarComponent,
    TopCategoriesNavbarSecondLevelComponent,
    AccountFlyoutFailoverComponent,
    MiniShoppingCartComponent,
    AccountFlyoutContextComponent,
    PromoImageLinkComponent,
    EditorialLinkListComponent,
    MenuNavigationBannerComponent,
    MobileMenuItemComponent,
    PromoCategoriesComponent,
    MiniShoppingCartEmptyComponent,
    HeaderBuyAgainComponent,
    HeaderYourAssortmentComponent,
    EmployeeProfileBoxComponent,
    EmployeeFlyoutComponent,
    MobileSidenavComponent,
    MobileParentCategoryNavigationComponent,
    CrawlableCategoriesComponent,
    NewBadgeComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    MatIconModule,
    MatInputModule,
    MmFlexLayoutModule,
    MmExpansionModule,
    MmButtonModule,
    TooltipModule,
    QaLocatorsModule,
    MmPopoverModule,
    BasePageModule,
    StoreModule.forFeature(BUYER_HEADER_FEATURE_NAME, reducers, { metaReducers }),
    EffectsModule.forFeature([
      SearchSuggestionsEffects,
      SearchEffects,
      CarouselEffects,
      CategoriesEffects,
      CategoryEffects,
    ]),
    MmIconModule,
    PriceTypeTogglerModule,
    ProductCardModule,
    CarouselModule,
    MmSpinnerModule,
    MmChipModule,
    LazyLoadImageModule,
    StoryblokEditableModule,
    DisableClickEventModule,
    ChevronRightModule,
    ChevronLeftModule,
    SearchModule,
    PhoneModule,
    NewsModule,
    MailModule,
    BasketPassiveModule,
    FrPostfixModule,
    CartNavigatorModule,
    CategoryBubbleModule,
    RegionSelectorModule,
    CoBrowseModule,
    ReBuyModule,
    ApprovalsModule,
    CartV2Module,
    ListModule,
    BoxModule,
    DesktopSwipeModule,
    CalenderModule,
    SuperSavingsModule,
    AllOffersModule
  ],
  exports: [
    HeaderComponent,
    HeaderToplineComponent,
    HeaderSimplifiedComponent,
    HeaderToplineLinksComponent,
    FooterComponent,
    FooterSimplifiedComponent,
    NavigationComponent,
    TopCategoriesNavbarComponent,
    TopCategoriesNavbarSecondLevelComponent,
    BoldPipe,
    MobileSidenavComponent,
    NewBadgeComponent
  ],
})
export class GenericModule {}
